// A record's chain, as a hierarchy of feed cards.
//
// Nesting IS the relationship: the receipt sits inside the order it was
// received against, the order inside the indent that raised it, the voucher
// inside the receipt. A branch collapses on its own twisty; the strip above
// collapses or expands the lot.
//
// **Depth costs 20px, and nothing else.** It used to cost ~51: a 24px icon
// rail, its gap, the twisty column, and a nested wrapper that handed the whole
// rail to the child again — three levels in and the card started a third of the
// way across the pane. Depth is now a hairline guide at `depth × 20 + 7`, with
// the twisty sitting ON that guide (a leaf gets a dot in the same 16px column,
// so cards line up whether or not they have children) and the object's name
// moved INSIDE the card as a kicker, where it costs no indent at all.
//
// A shut branch says what it is holding back, because a twisty alone does not:
// "3 hidden" beside the object name, and the twisty takes the accent.
//
// A step the viewer may not read keeps its place AND its children — breaking
// the chain there would leave two disconnected halves and hide the far end they
// are entitled to.

import { ChevronRight, FileText } from 'lucide-react'

import FeedCard, { stampText } from './FeedCard'
import { MENU_Z_OVER_MODAL, type RowAction } from '../list/RowActionsMenu'
import type { FeedBody, TrailNode } from './contract'
import { descendantCount, nodeKey } from './trailNodes'

/** One level of nesting. The whole point of this file's rewrite. */
const STEP = 20
/** The twisty column: wide enough for the 15px control, narrow enough that a
 *  leaf's dot does not shift its card away from its siblings'. */
const RAIL = 16

export interface TrailTreeProps {
  nodes: TrailNode[]
  /** Which branches are collapsed, by `obj_name:txn_id`. */
  collapsed: Record<string, boolean>
  onToggleBranch: (key: string) => void
  /** Which card bodies are open, and what has been fetched for them. */
  bodies: Record<string, { open: boolean; busy?: boolean; body?: FeedBody | null }>
  onToggleBody: (node: TrailNode) => void
  selectedKey?: string
  onOpen?: (node: TrailNode) => void
  /** The kebab for one record — the host's own actions, already rights-gated.
   *  The same menu the design view draws, because it is the same record. */
  actionsFor?: (node: TrailNode) => RowAction[]
  depth?: number
}

export default function TrailTree({
  nodes, collapsed, onToggleBranch, bodies, onToggleBody, selectedKey, onOpen,
  actionsFor, depth = 0,
}: TrailTreeProps) {
  return (
    <>
      {nodes.map((node) => {
        const key = nodeKey(node)
        const kids = node.children || []
        const open = !collapsed[key]
        const state = bodies[key]
        const buried = kids.length > 0 && !open ? descendantCount(node) : 0
        const selected = selectedKey === key
        const when = node.stamp?.when ? stampText(node.stamp.when) : ''
        const who = node.stamp?.who || ''

        return (
          <div key={key}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              {/* depth, drawn as guides rather than paid for in padding */}
              <div style={{ flex: 'none', width: depth * STEP, position: 'relative' }}>
                {Array.from({ length: depth }, (_, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: 0, bottom: 0, left: i * STEP + 7, width: 1,
                    background: 'var(--color-border)',
                  }} />
                ))}
              </div>

              <div style={{
                flex: 'none', width: RAIL, display: 'flex', justifyContent: 'center',
                paddingTop: 12,
              }}>
                {kids.length > 0 ? (
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} what follows ${node.title}`}
                    onClick={() => onToggleBranch(key)}
                    style={{
                      width: 15, height: 15, padding: 0, display: 'grid', placeItems: 'center',
                      borderRadius: 4, cursor: 'pointer',
                      border: `1px solid ${buried
                        ? 'var(--color-accent, #0061D0)' : 'var(--color-border)'}`,
                      background: buried
                        ? 'var(--color-accent-weak, rgba(0,97,208,0.10))'
                        : 'var(--color-bg, #fff)',
                      color: buried
                        ? 'var(--color-accent, #0061D0)' : 'var(--color-text-secondary)',
                    }}
                  >
                    <ChevronRight
                      size={10}
                      style={{ transform: open ? 'rotate(90deg)' : undefined }}
                    />
                  </button>
                ) : (
                  <span style={{
                    width: 5, height: 5, marginTop: 5, borderRadius: 999,
                    background: 'var(--color-border-strong, #c7cfd9)',
                  }} />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0, padding: '0 0 6px 6px' }}>
                <FeedCard
                  card={node.card}
                  restricted={node.restricted}
                  exists={node.exists}
                  title={node.title}
                  anchor={node.is_anchor}
                  selected={selected}
                  barColor={selected
                    ? 'var(--color-accent, #0061D0)'
                    : node.is_anchor
                      ? 'var(--color-accent-weak, rgba(0,97,208,0.35))'
                      : 'var(--color-border)'}
                  kicker={{
                    title: node.title,
                    meta: [when, who].filter(Boolean).join(' · '),
                    chips: (
                      <>
                        {node.is_anchor && (
                          <span style={{
                            flex: 'none', whiteSpace: 'nowrap',
                            padding: '1px 6px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                            background: 'var(--color-accent-weak, rgba(0,97,208,0.10))',
                            color: 'var(--color-accent, #0061D0)',
                          }}>you are here</span>
                        )}
                        {buried > 0 && (
                          <button
                            type="button"
                            title={`Show what ${node.title} raised`}
                            onClick={(e) => { e.stopPropagation(); onToggleBranch(key) }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              flex: 'none', whiteSpace: 'nowrap',
                              padding: '1px 7px', borderRadius: 999, cursor: 'pointer',
                              border: '1px solid var(--color-border-strong, #c7cfd9)',
                              background: 'var(--color-surface-2, #f0f3f7)',
                              color: 'var(--color-text-secondary)',
                              fontSize: 13, fontWeight: 600,
                            }}
                          >
                            <FileText size={9} />
                            {buried} hidden
                          </button>
                        )}
                      </>
                    ),
                  }}
                  body={state?.body}
                  bodyOpen={state?.open}
                  bodyBusy={state?.busy}
                  onToggleBody={node.card?.has_body ? () => onToggleBody(node) : undefined}
                  onOpen={node.txn_id && onOpen ? () => onOpen(node) : undefined}
                  actions={actionsFor?.(node) || []}
                  actionsZIndex={MENU_Z_OVER_MODAL}
                />
              </div>
            </div>

            {kids.length > 0 && open && (
              <TrailTree
                nodes={kids}
                collapsed={collapsed}
                onToggleBranch={onToggleBranch}
                bodies={bodies}
                onToggleBody={onToggleBody}
                selectedKey={selectedKey}
                onOpen={onOpen}
                actionsFor={actionsFor}
                depth={depth + 1}
              />
            )}
          </div>
        )
      })}
    </>
  )
}
